import type { Object3D } from 'three';
import { SCENE_OBJECT_CHILD_ORDER_CHANGED_EVENT } from '../../utils/scene-object/addObject3DToParent';

/**
 * 跟踪一个物理场景子树的成员，并为多个领域共享同一套 Three.js 增删监听。
 *
 * 只在成员增删时访问变化子树，不做渲染、矩阵更新、几何释放或语义判定。
 */
export class SceneObjectMembershipTracker implements SceneObjectMembershipSource {
  /** 已实际挂载并安装监听的对象。 */
  private readonly members = new Set<Object3D>();
  /** Three.js UUID 到当前物理成员的稳定索引。 */
  private readonly objectByUuid = new Map<string, Object3D>();
  /** 显式订阅成员变化的非拥有型消费者。 */
  private readonly observers = new Set<SceneObjectMembershipObserver>();
  /** 累计 UUID 索引读取次数，仅供显式性能统计。 */
  private uuidLookups = 0;
  /** 累计同父级 children 顺序变化次数。 */
  private orderChanges = 0;

  /** @param root 所跟踪的场景根；其对象与资源仍由调用方拥有。 */
  public constructor(private root: Object3D | null) {
    if (root) this.observeTree(root);
  }

  /**
   * 回放现有成员并开始订阅；回放不遍历场景树。
   * @param observer 只处理成员变化的消费者。
   * @returns 幂等退订函数，不销毁跟踪器。
   */
  public subscribe(observer: SceneObjectMembershipObserver): () => void {
    if (!this.root) throw new Error('场景成员跟踪器已释放');
    if (this.observers.has(observer)) throw new Error('场景成员消费者重复订阅');
    // 固定回放集合，避免消费者在回调中新增成员时被事件和回放重复通知。
    const existingMembers = Array.from(this.members);
    this.observers.add(observer);
    try {
      for (const object of existingMembers) {
        if (this.members.has(object)) observer.onObjectAdded(object);
      }
    } catch (error) {
      this.observers.delete(observer);
      throw error;
    }
    return () => { this.observers.delete(observer); };
  }

  /**
   * 按 Three.js 稳定 UUID 定位当前场景成员。
   *
   * 设计说明：
   * 1. UUID 是 Object3D 的运行时身份，成员增删时与监听集合原子维护；
   * 2. 若外部错误改写 UUID，旧键校验会立即失效，调用方仍可走保守遍历兜底；
   * 3. 本索引不按 name 建表，因为业务允许重命名且 Three.js 不派发名称变化事件。
   *
   * @param objectUuid 待定位的运行时 UUID。
   * @returns 当前仍属于场景的对象；未命中时返回 null。
   */
  public resolveObjectByUuid(objectUuid: string): Object3D | null {
    this.uuidLookups++;
    const object = this.objectByUuid.get(objectUuid) ?? null;
    if (!object || object.uuid === objectUuid) return object;
    this.objectByUuid.delete(objectUuid);
    return null;
  }

  /** @returns 只在显式调试读取时创建的统计对象。 */
  public getStats(): SceneObjectMembershipStats {
    return {
      members: this.members.size,
      subscribers: this.observers.size,
      uuidLookups: this.uuidLookups,
      orderChanges: this.orderChanges,
    };
  }

  /** 幂等解除监听与引用，不从用户场景移除对象或释放 GPU 资源。 */
  public dispose(): void {
    if (!this.root) return;
    this.root = null;
    for (const object of this.members) this.unobserveObject(object);
    this.objectByUuid.clear();
    this.observers.clear();
  }

  /** @param event Three.js 已完成挂载的子树变化。 */
  private readonly handleChildAdded = (event: SceneObjectMembershipChildEvent): void => {
    this.observeTree(event.child);
  };

  /** @param event Three.js 已完成解除挂载的子树变化。 */
  private readonly handleChildRemoved = (event: SceneObjectMembershipChildEvent): void => {
    // removed 监听中可能已重新挂到同一场景；以当前物理父链为准，不删除新归属。
    const pending = [event.child];
    while (pending.length > 0) {
      const object = pending.pop()!;
      for (let index = object.children.length - 1; index >= 0; index--) pending.push(object.children[index]);
      if (!this.belongsToRoot(object)) this.unobserveObject(object);
    }
  };

  /** Three.js 同父级 children 重排后的轻量通知，不修改成员集合。 */
  private readonly handleChildOrderChanged = (): void => {
    this.orderChanges += 1;
    for (const observer of this.observers) observer.onObjectOrderChanged?.();
  };

  /** @param root 刚进入场景的子树，只遍历这次变化范围。 */
  private observeTree(root: Object3D): void {
    const pending = [root];
    while (pending.length > 0) {
      const object = pending.pop()!;
      if (!this.belongsToRoot(object)) continue;
      // 先固定子树访问路径，消费者的增删由对应事件处理，避免重入破坏遍历顺序。
      for (let index = object.children.length - 1; index >= 0; index--) pending.push(object.children[index]);
      if (this.members.has(object)) continue;
      object.addEventListener('childadded', this.handleChildAdded);
      object.addEventListener('childremoved', this.handleChildRemoved);
      object.addEventListener(
        SCENE_OBJECT_CHILD_ORDER_CHANGED_EVENT as never,
        this.handleChildOrderChanged as never,
      );
      this.members.add(object);
      this.objectByUuid.set(object.uuid, object);
      for (const observer of this.observers) observer.onObjectAdded(object);
    }
  }

  /** @param object 已离开的成员，重复解除不会重复通知。 */
  private unobserveObject(object: Object3D): void {
    if (!this.members.delete(object)) return;
    object.removeEventListener('childadded', this.handleChildAdded);
    object.removeEventListener('childremoved', this.handleChildRemoved);
    object.removeEventListener(
      SCENE_OBJECT_CHILD_ORDER_CHANGED_EVENT as never,
      this.handleChildOrderChanged as never,
    );
    if (this.objectByUuid.get(object.uuid) === object) this.objectByUuid.delete(object.uuid);
    for (const observer of this.observers) observer.onObjectRemoved(object);
  }

  /** @param object 待验证成员。 @returns 当前仍能追溯到所跟踪根时为 true。 */
  private belongsToRoot(object: Object3D): boolean {
    let current: Object3D | null = object;
    while (current) {
      if (current === this.root) return true;
      current = current.parent;
    }
    return false;
  }
}
